import { ROOMS } from '../rooms';
import { IS_IOS } from '../config';

// ATMOSPHERE — the city's music sounding like where it is (2026-11-05).
//
// Khari: "is it possible to modify sounds so they sound more atmospheric? For
// example, if we want the plaza or the music at the basketball courts to sound
// like their environments" — and, after the Warehouse played clean: "there is
// no atmospheric change to it."
//
// Each music player is routed through Web Audio (one context for the World):
//
//   element → the SPEAKER (what is playing it: the plaza's PA, a portable
//   speaker at the court, a car stereo heard through its body, the record
//   store's deck through the door, a room's sound system) → DISTANCE (the top
//   end goes first as you walk away; a building in between muffles it) →
//   DIRECTION (left/right from where you face) → dry, plus a send to the PLACE
//   (an echo generated here: the plaza's slaps off the buildings, the fence and
//   concrete at the court, a concrete club, a gym's hard walls, a café) → out.
//   Further away = less of the source, more of the place.
//
// ⚠ ONLY WHERE IT IS SAFE, ELSE EXACTLY AS BEFORE:
//   · Only files from buckets that let our origins read them (CORS, set
//     2026-11-05 on hypetunes_tracks and hypetunes_radio). A file Web Audio may
//     not read plays SILENT through it, so anything else stays a plain player.
//     A bucket that fails once is left plain for the rest of the visit.
//   · Not on iOS (volume read back as 1): the iPhone keeps its plain players and
//     the voices-first rule (city/hearing); its audio works and stays that way.
//   · Only once the context is running (a tap has happened): a routed player
//     on a suspended context is silent, so before that a player stays plain.
//   · A routed element is routed for life (createMediaElementSource), so these
//     players are their own small pool, never lent out for anything else.
//
// Callers: services/audioPool `takeMusic(url, space)` to borrow one, hearing
// `setLevel` (its gain, here), `placeSound(el, { dist, walled, pan })` each tick.

// Never on iOS (by name: city/hearing says why), nor anywhere volume reads back wrong.
const VOLUME_WORKS = !IS_IOS && (() => {
  try { const a = document.createElement('audio'); a.volume = 0.5; return Math.abs(a.volume - 0.5) < 0.01; } catch { return false; }
})();
const AC = typeof window !== 'undefined' ? (window.AudioContext || window.webkitAudioContext) : null;
const BUCKETS = /^https:\/\/(?:storage\.googleapis\.com\/(hypetunes_tracks|hypetunes_radio)\/|(hypetunes_tracks|hypetunes_radio)\.storage\.googleapis\.com\/)/;
const POOL_MAX = 8;

// ── The places ─────────────────────────────────────────────────────────────
// `ir`: the echo (seconds of tail, pre-delay, damping Hz, early reflections
// [seconds, level]); `wet`: [near, far] send; `tone`: the speaker's filters;
// `drive`: a little crunch (a small speaker pushed); `comp`: a PA's compressor.
const SPACES = {
  plaza: {
    ir: { secs: 1.3, pre: 0.02, damp: 5200, early: [[0.11, 0.55], [0.19, 0.34], [0.27, 0.2]] },
    wet: [0.22, 0.6], comp: true,
    tone: [['highpass', 45], ['lowshelf', 110, 2], ['lowpass', 16000]],
  },
  court: {
    ir: { secs: 0.7, pre: 0.008, damp: 6500, early: [[0.035, 0.6], [0.07, 0.38], [0.105, 0.22]] },
    wet: [0.18, 0.5], drive: 0.35,
    tone: [['highpass', 150], ['peaking', 1300, 3, 0.9], ['lowpass', 7500]],
  },
  car: {
    ir: { secs: 0.45, pre: 0.005, damp: 4000, early: [[0.02, 0.4]] },
    wet: [0.1, 0.35],
    tone: [['lowshelf', 90, 6], ['lowpass', 2300, 0, 0.8]],
  },
  store: {
    ir: { secs: 0.8, pre: 0.01, damp: 4500, early: [[0.025, 0.42], [0.05, 0.26]] },
    wet: [0.3, 0.55],
    tone: [['peaking', 900, 2, 0.8], ['lowpass', 5200]],
  },
  concrete: {   // the Warehouse, and every club made from it (the Blackroom)
    ir: { secs: 1.5, pre: 0.012, damp: 5000, early: [[0.02, 0.5], [0.045, 0.36], [0.08, 0.22]] },
    wet: [0.42, 0.42], comp: true,
    tone: [['lowshelf', 70, 3]],
  },
  gym: {
    ir: { secs: 1.7, pre: 0.015, damp: 7500, early: [[0.03, 0.46], [0.06, 0.3]] },
    wet: [0.45, 0.45],
    tone: [],
  },
  cafe: {
    ir: { secs: 0.6, pre: 0.006, damp: 5500, early: [[0.015, 0.35]] },
    wet: [0.22, 0.22],
    tone: [['lowpass', 10000]],
  },
  room: {
    ir: { secs: 1.0, pre: 0.01, damp: 6000, early: [[0.02, 0.4], [0.04, 0.25]] },
    wet: [0.3, 0.3],
    tone: [],
  },
};
/** A room's acoustics: its own `acoustics`, else what it was made from (a club → the Warehouse's). */
const ROOM_SPACES = { warehouse: 'concrete', gym: 'gym', jahva: 'cafe', hq: 'room', salon: 'room', printshop: 'room', grocery: 'store' };
export function spaceForRoom(roomId) {
  const r = ROOMS[roomId];
  const key = r?.acoustics || r?.venueBase || roomId;
  return SPACES[key] ? key : ROOM_SPACES[key] || ROOM_SPACES[roomId] || 'room';
}

// ── The context ────────────────────────────────────────────────────────────
let ctx = null;
let out = null;              // everything ends here
const buses = new Map();     // space → { input }
const chains = new WeakMap(); // element → its nodes
const free = [];              // routed elements, idle
const mine = new WeakSet();   // every element routed here
const badBuckets = new Set(); // a bucket that would not let us read it: plain from now on
export const atmosStats = { routed: 0, plain: 0, lent: 0 };

function ensureCtx() {
  if (ctx || !AC || !VOLUME_WORKS) return ctx;
  try {
    ctx = new AC();
    out = ctx.createGain();
    out.gain.value = 1;
    out.connect(ctx.destination);
  } catch (err) {
    console.warn('[atmos] no audio context:', err?.message || err);
    ctx = null;
  }
  return ctx;
}
// A tap wakes it (browsers start a context suspended until one).
if (typeof window !== 'undefined' && AC && VOLUME_WORKS) {
  const wake = () => { const c = ensureCtx(); if (c && c.state !== 'running') c.resume().catch(() => {}); };
  for (const type of ['pointerup', 'keydown', 'touchend', 'click']) window.addEventListener(type, wake, { capture: true, passive: true });
}

const bucketOf = (url) => { const m = BUCKETS.exec(String(url || '')); return m ? (m[1] || m[2]) : null; };
/** Is this file in a bucket that lets the World read it (CORS)? Any platform. */
export function readableUrl(url) {
  const b = bucketOf(url);
  return !!b && !badBuckets.has(b);
}

/** Can this file be heard through its place? (See ONLY WHERE IT IS SAFE.) */
export function canRoute(url) {
  const b = bucketOf(url);
  if (!b || badBuckets.has(b) || !VOLUME_WORKS) return false;
  const c = ensureCtx();
  return !!c && c.state === 'running';
}

/** A short impulse response, made once per place: noise that dies away, darker as it goes, after its early reflections. */
function makeIR({ secs, pre = 0.01, damp = 6000, early = [] }) {
  const rate = ctx.sampleRate;
  const p0 = Math.floor(pre * rate);
  const len = p0 + Math.floor(secs * rate);
  const buf = ctx.createBuffer(2, len, rate);
  for (let ch = 0; ch < 2; ch++) {
    const d = buf.getChannelData(ch);
    let lp = 0;
    for (let i = p0; i < len; i++) {
      const t = (i - p0) / rate;
      const k = t / secs;
      // Down 60 dB over `secs` (how a room's echo is measured), faded to nothing in the last 5%.
      const env = Math.exp(-6.9 * k) * Math.min(1, (1 - k) / 0.05);
      const fc = damp * (1 - 0.75 * k);   // the top end dies first
      const a = 1 - Math.exp((-2 * Math.PI * fc) / rate);
      lp += a * ((Math.random() * 2 - 1) - lp);
      d[i] = lp * env * 0.6;
    }
    // Early reflections: a wall, a building, the fence. Slightly apart in each ear.
    for (const [at, g] of early) {
      const i0 = p0 + Math.floor((at + ch * 0.0009) * rate);
      for (let j = 0; j < 48 && i0 + j < len; j++) d[i0 + j] += (Math.random() * 2 - 1) * g * Math.exp(-j / 10);
    }
  }
  // Unit energy, set here rather than by the convolver's own `normalize` (which
  // left the echo ~25 dB under the music, too quiet to notice): the place's
  // `wet` then means what it says, its echo that much under the source.
  let e = 0;
  for (let ch = 0; ch < 2; ch++) { const d = buf.getChannelData(ch); for (let i = 0; i < len; i++) e += d[i] * d[i]; }
  const k = e > 0 ? 1 / Math.sqrt(e / 2) : 1;
  for (let ch = 0; ch < 2; ch++) { const d = buf.getChannelData(ch); for (let i = 0; i < len; i++) d[i] *= k; }
  return buf;
}

function busFor(space) {
  if (buses.has(space)) return buses.get(space);
  const def = SPACES[space] || SPACES.room;
  const conv = ctx.createConvolver();
  conv.normalize = false;   // makeIR sets its own level
  conv.buffer = makeIR(def.ir);
  const ret = ctx.createGain();
  ret.gain.value = 1;
  conv.connect(ret).connect(out);
  const bus = { input: conv };
  buses.set(space, bus);
  return bus;
}

/** The element's nodes, made once: source → tone → distance → pan → (dry → out) + (send → place). */
function chainFor(el) {
  let c = chains.get(el);
  if (c) return c;
  const src = ctx.createMediaElementSource(el);
  const level = ctx.createGain();
  const dist = ctx.createBiquadFilter(); dist.type = 'lowpass'; dist.frequency.value = 18000; dist.Q.value = 0.5;
  const pan = ctx.createStereoPanner ? ctx.createStereoPanner() : null;
  const dry = ctx.createGain();
  const send = ctx.createGain();
  const tone = ctx.createGain();   // where the speaker's filters start (rebuilt per place)
  src.connect(level).connect(tone);
  const tail = dist;
  if (pan) { tail.connect(pan); pan.connect(dry); pan.connect(send); } else { tail.connect(dry); tail.connect(send); }
  dry.connect(out);
  c = { src, level, tone, dist, pan, dry, send, toneNodes: [], space: null, wet: [0.2, 0.2], indoor: false };
  chains.set(el, c);
  return c;
}

function curve(drive) {
  const n = 1024; const k = 1 + drive * 8; const cv = new Float32Array(n);
  for (let i = 0; i < n; i++) { const x = (i / (n - 1)) * 2 - 1; cv[i] = Math.tanh(k * x) / Math.tanh(k); }
  return cv;
}

/** Put the element's chain in `space`: its speaker's tone, its send to that place. */
function setSpace(c, space) {
  if (c.space === space) return;
  const def = SPACES[space] || SPACES.room;
  try { c.tone.disconnect(); } catch { /* first time */ }
  for (const n of c.toneNodes) { try { n.disconnect(); } catch { /* gone */ } }
  c.toneNodes = [];
  let at = c.tone;
  for (const [type, f, g = 0, q] of def.tone || []) {
    const b = ctx.createBiquadFilter();
    b.type = type; b.frequency.value = f; b.gain.value = g;
    if (q != null) b.Q.value = q;
    at.connect(b); at = b; c.toneNodes.push(b);
  }
  if (def.drive) { const w = ctx.createWaveShaper(); w.curve = curve(def.drive); w.oversample = '2x'; at.connect(w); at = w; c.toneNodes.push(w); }
  if (def.comp) {
    const k = ctx.createDynamicsCompressor();
    k.threshold.value = -18; k.ratio.value = 3; k.attack.value = 0.01; k.release.value = 0.2;
    at.connect(k); at = k; c.toneNodes.push(k);
  }
  at.connect(c.dist);
  try { c.send.disconnect(); } catch { /* first time */ }
  c.send.connect(busFor(space).input);
  c.space = space;
  c.wet = def.wet || [0.2, 0.2];
  c.indoor = def.wet && def.wet[0] === def.wet[1];
  placeChain(c, { dist: 0, walled: false, pan: 0 }, true);
}

function placeChain(c, { dist = 0, walled = false, pan = 0 }, now = false) {
  const t = ctx.currentTime; const tc = now ? 0.001 : 0.12;
  const k = Math.max(0, Math.min(1, Number(dist) || 0));
  let fc = 1800 + 16200 * Math.pow(1 - k, 2);   // the air takes the top end first
  if (walled) fc = Math.min(fc, 650);           // through a building
  c.dist.frequency.setTargetAtTime(fc, t, tc);
  const [w0, w1] = c.wet;
  c.send.gain.setTargetAtTime(w0 + (w1 - w0) * k, t, tc);
  c.dry.gain.setTargetAtTime(1 - 0.45 * k, t, tc);
  if (c.pan) c.pan.pan.setTargetAtTime(Math.max(-0.85, Math.min(0.85, Number(pan) || 0)), t, tc);
}

/**
 * An element for `url`, heard through `space` — or null when it should be a
 * plain player (the caller then takes an ordinary one). For audioPool only.
 */
export function atmosTake(url, space) {
  if (!canRoute(url)) { atmosStats.plain += 1; return null; }
  let el = free.pop();
  try {
    if (!el) {
      el = new Audio();
      el.crossOrigin = 'anonymous';   // before any src: Web Audio may only read what CORS lets it
      el.preload = 'auto';
      mine.add(el);
      chainFor(el);
      atmosStats.routed += 1;
    }
    const c = chains.get(el);
    setSpace(c, SPACES[space] ? space : 'room');
    c.level.gain.value = 0;
    el.loop = false;
    el.muted = false;
    el.volume = 1;
    el.src = url;
    const b = bucketOf(url);
    el.addEventListener('error', () => {
      if (!el.error || badBuckets.has(b)) return;
      badBuckets.add(b);
      console.warn(`[atmos] ${b} would not let the World read a file (media error ${el.error.code}); its music plays plain from now on`);
    }, { once: true });
  } catch (err) {
    console.warn('[atmos] could not route a player:', err?.message || err);
    return null;
  }
  atmosStats.lent += 1;
  return el;
}

/** Is this one of ours (audioPool.releaseAudio hands it back here)? */
export const atmosOwns = (el) => !!el && mine.has(el);

/** Handed back: silent, its file let go, kept for the next song. */
export function atmosGive(el) {
  if (!atmosOwns(el)) return;
  atmosStats.lent = Math.max(0, atmosStats.lent - 1);
  const c = chains.get(el);
  try { c.level.gain.cancelScheduledValues(0); c.level.gain.value = 0; } catch { /* ignore */ }
  try { el.pause(); } catch { /* ignore */ }
  try { el.removeAttribute('src'); el.load(); } catch { /* ignore */ }
  if (free.length < POOL_MAX) free.push(el);
}

/** Its loudness, for hearing.setLevel: true when this element is ours (its gain carries it). */
export function atmosLevel(el, v) {
  const c = atmosOwns(el) ? chains.get(el) : null;
  if (!c) return false;
  if (el.muted) el.muted = false;   // the gain is the silence now
  try { if (el.volume !== 1) el.volume = 1; } catch { /* ignore */ }
  const x = Math.max(0, Math.min(1, Number(v) || 0));
  c.level.gain.setTargetAtTime(x < 0.01 ? 0 : x, ctx.currentTime, 0.06);
  return true;
}

/**
 * Where it is heard from, each tick: `dist` 0 (at it) … 1 (the edge of
 * earshot), `walled` (a building between), `pan` −1 … 1 (left … right). A
 * plain player ignores it.
 */
export function placeSound(el, where) {
  const c = atmosOwns(el) ? chains.get(el) : null;
  if (c) placeChain(c, where || {});
}

/** Left/right of a point for this camera (its own facing): −1 … 1; 0 when there is no facing. */
export function panFrom(camera, x, z) {
  const m = camera?.matrixWorld?.elements;
  if (!m) return 0;
  const dx = x - m[12]; const dz = z - m[14];
  const L = Math.hypot(dx, dz);
  if (L < 0.3) return 0;
  // The camera's right, flattened onto the ground.
  const rx = m[0]; const rz = m[2]; const rl = Math.hypot(rx, rz) || 1;
  return ((dx / L) * (rx / rl) + (dz / L) * (rz / rl)) * Math.min(1, L / 2);
}

/** An analyser on everything heard through here (tests and debugging; nothing calls it in the World). */
export function atmosTap() {
  if (!ensureCtx()) return null;
  const a = ctx.createAnalyser();
  a.fftSize = 2048;
  out.connect(a);
  return a;
}

/** For ?audiodebug. */
export function atmosDebug() {
  return {
    on: VOLUME_WORKS && !!AC, state: ctx ? ctx.state : 'none',
    routed: atmosStats.routed, lent: atmosStats.lent, plain: atmosStats.plain, plainBuckets: [...badBuckets],
  };
}
